import {
  differenceInCalendarDays,
  format,
  fromUnixTime,
  getHours,
  getMinutes,
  getSeconds,
} from "date-fns";

import type { AnalyticsData, AnalysisNode } from "../../api/types";
import { parseUtcDate } from "../../utils";
import { getTaskColor, INSIGNIFICANT_COLOR } from "./palette";

const L2 = 2;
const L3 = 3;
const L4 = 4;

// A gap left between node coverage only counts as idle ("away from keyboard")
// once it reaches this length; shorter holes are dropped, not surfaced as idle.
const IDLE_MIN_SECONDS = 120;

// A reusable label per step occurrence, reconstructed from the node hierarchy so
// the analytics tabs keep their existing shape. `events` are the instance's L3
// steps, each spanning its folded coverage (RFC3339, so `parseUtcDate` works).
export type ReconstructedTrace = {
  name: string;
  description: string;
  significance: boolean;
  events: { name: string; goal?: string; start: string; end: string; screenshots?: string[] }[];
};

export type ProcessView = {
  trace: ReconstructedTrace;
  color: string;
  totalMin: number;
  eventCount: number;
  // Per-app active seconds inside this one instance, folded from its L2 descendants'
  // `app.<app>.duration_s` (same fold that feeds `appUsage`). Drives the by-app
  // filter and the per-instance time-in-app figure it shows. Empty for the synthetic
  // orphan process, which is never filterable.
  appSeconds: Map<string, number>;
  // Wall-clock extent of the instance, folded from its L2 descendants' coverage
  // (RFC3339). Kept separate from `trace.events` so a run whose steps were all
  // filtered out (eventCount 0) can still show a time range. `undefined` only
  // when no descendant coverage survived, leaving nothing to anchor a range to.
  extent?: { start: string; end: string };
};

export type EventSegment = {
  processName: string;
  traceIndex: number;
  eventName: string;
  eventIndex?: number;
  color: string;
  start: number;
  end: number;
  startStr: string;
  endStr: string;
  dur: number;
};

export type IdleSegment = {
  start: number; // minutes since midnight (local time)
  end: number;
  dur: number; // minutes
  startLabel: string; // "HH:MM:SS"
  endLabel: string;
};

// One app's total active time across the recording, in minutes. Folded from the
// `app.<app>.duration_s` node attributes over the frontier (see `buildAnalyticsModel`).
export type AppUsage = { name: string; totalMin: number };

export type AnalyticsModel = {
  processes: ProcessView[];
  idleSegments: IdleSegment[];
  allEvents: EventSegment[];
  totalActive: number;
  // Time per app, descending. Empty for recordings analyzed before the app-usage
  // enricher existed — the caller uses this emptiness to hide the app view.
  appUsage: AppUsage[];
  // The summarize stage's LLM-written caption, when it produced one. Absent for
  // recordings analyzed before that stage existed, or when it found nothing to
  // summarize or failed (best-effort).
  sessionSummary?: string;
};

const dateToLocalMin = (d: Date): number => getHours(d) * 60 + getMinutes(d) + getSeconds(d) / 60;

const epochToTimeLabel = (epoch: bigint | number): string =>
  format(fromUnixTime(Number(epoch)), "HH:mm:ss");

// `[startEpoch, endEpoch]` in seconds. Coverage is folded in this space so the
// math mirrors the pipeline's `merge_adjacent`.
type Interval = [number, number];

const isoToEpoch = (iso: string): number => parseUtcDate(iso).getTime() / 1000;
const epochToIso = (epoch: number): string => new Date(epoch * 1000).toISOString();

// Union touching/overlapping segments into contiguous runs — the client-side twin
// of the pipeline's coverage fold.
const mergeAdjacent = (intervals: Interval[]): Interval[] => {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const out: Interval[] = [];
  for (const [s, e] of sorted) {
    const last = out[out.length - 1];
    if (last && last[1] >= s) {
      last[1] = Math.max(last[1], e);
    } else {
      out.push([s, e]);
    }
  }
  return out;
};

// L4 process instances, in document order. Exposed for the recordings list, the
// static demo's session summaries, and the session-title helpers, which only
// need the instance count/names.
export const processInstances = (data: AnalyticsData): AnalysisNode[] =>
  (data.nodes ?? []).filter(node => node.depth === L4);

// Per-app active seconds live on L2 (activity) nodes as `app.<app>.duration_s`
// attributes, written by the enricher. Recover `<app>` from such a key, or null when
// `name` isn't one. Mirrors the Rust `app_usage_attr` convention; `<app>` may itself
// contain dots (e.g. "zoom.us").
const APP_ATTR_PREFIX = "app.";
const APP_ATTR_SUFFIX = ".duration_s";
const appAttrName = (name: string): string | null => {
  if (!name.startsWith(APP_ATTR_PREFIX) || !name.endsWith(APP_ATTR_SUFFIX)) return null;
  const app = name.slice(APP_ATTR_PREFIX.length, name.length - APP_ATTR_SUFFIX.length);
  return app.length > 0 ? app : null;
};

export const buildAnalyticsModel = (data: AnalyticsData): AnalyticsModel => {
  // Preserve the calendar-day offset, including sessions longer than 24 hours.
  const sessionStart = fromUnixTime(Number(data.startTimestamp));
  const epochToWrappedMin = (epoch: number) => {
    const date = fromUnixTime(epoch);
    return dateToLocalMin(date) + differenceInCalendarDays(date, sessionStart) * 1440;
  };

  // Every node by id, across the connected tree and the orphans, so a step can
  // resolve its L2 children (the only level that carries coverage).
  const byId = new Map<number, AnalysisNode>();
  for (const node of data.nodes ?? []) byId.set(node.id, node);
  for (const node of data.orphans ?? []) byId.set(node.id, node);

  const nodeCoverage = (node: AnalysisNode): Interval[] =>
    (node.coverage ?? []).map(([s, e]) => [isoToEpoch(s), isoToEpoch(e)] as Interval);

  // The folded coverage of any node, regardless of level — the single source of a
  // node's wall-clock span used everywhere (steps, instances, orphans, idle). L2
  // carries coverage directly; higher levels recover it by folding descendants
  // down to L2 (an L3 step from its L2 children, an L4 instance from its steps'),
  // so an empty result means no covered time survived under the node.
  const resolveCoverage = (node: AnalysisNode): Interval[] => {
    const own = nodeCoverage(node);
    if (own.length > 0) return mergeAdjacent(own);

    const segments: Interval[] = [];
    for (const childId of node.children ?? []) {
      const child = byId.get(childId);
      if (child) segments.push(...resolveCoverage(child));
    }
    return mergeAdjacent(segments);
  };

  const nodeAppSeconds = (node: AnalysisNode): Map<string, number> => {
    const totals = new Map<string, number>();
    for (const attr of node.attributes ?? []) {
      const app = appAttrName(attr.name);
      if (app !== null && typeof attr.value === "number") {
        totals.set(app, (totals.get(app) ?? 0) + attr.value);
      }
    }
    return totals;
  };

  // The folded per-app active seconds of any node — the app analog of
  // `resolveCoverage`. L2 carries `app.<app>.duration_s` directly; higher levels
  // recover it by summing their descendants down to L2. A plain sum with no
  // threshold or cap (the enricher already thresholded within each L2 node), so a
  // shared step is credited to each parent just like its `duration_s` is.
  const resolveAppSeconds = (node: AnalysisNode): Map<string, number> => {
    const own = nodeAppSeconds(node);
    if (own.size > 0) return own;

    const totals = new Map<string, number>();
    for (const childId of node.children ?? []) {
      const child = byId.get(childId);
      if (!child) continue;
      for (const [app, secs] of resolveAppSeconds(child)) {
        totals.set(app, (totals.get(app) ?? 0) + secs);
      }
    }
    return totals;
  };

  // An L3 step's screenshots = the fold of its L2 (activity) children's
  // `screenshots` attribute, concatenated in child order (each child's list is
  // already chronological). Only L2 carries the attribute, so a step recovers it
  // the same way it recovers coverage — by walking its children.
  const nodeScreenshots = (node: AnalysisNode): string[] => {
    const value = node.attributes?.find(a => a.name === "screenshots")?.value;
    return Array.isArray(value) ? value : [];
  };
  const stepScreenshots = (step: AnalysisNode): string[] => {
    const shots: string[] = [];
    for (const childId of step.children ?? []) {
      const child = byId.get(childId);
      if (child) shots.push(...nodeScreenshots(child));
    }
    return shots;
  };

  const allEvents: EventSegment[] = [];
  // All session views group runs by process name. Assign colors at that same
  // level so choosing a different run (by duration or app filter) cannot change it.
  const processColors = new Map<string, string>();
  const processes: ProcessView[] = processInstances(data).map((instance, traceIndex) => {
    const processName = instance.name ?? "";
    let color = processColors.get(processName);
    if (color === undefined) {
      color = getTaskColor(processColors.size);
      processColors.set(processName, color);
    }
    const events: ReconstructedTrace["events"] = [];

    for (const stepId of instance.children ?? []) {
      const step = byId.get(stepId);
      if (!step || step.depth !== L3) continue;

      const eventName = step.name ?? "";
      const segments = resolveCoverage(step);
      if (segments.length === 0) continue;

      // One step row for the overview list, spanning its (possibly gapped) extent.
      events.push({
        name: eventName,
        goal: step.goal ?? "",
        start: epochToIso(segments[0][0]),
        end: epochToIso(segments[segments.length - 1][1]),
        screenshots: stepScreenshots(step),
      });

      // One timeline bar per disjoint segment — this is how interleaving shows.
      for (const [s, e] of segments) {
        const start = epochToWrappedMin(s);
        const end = epochToWrappedMin(e);
        allEvents.push({
          processName,
          traceIndex,
          eventName,
          eventIndex: events.length - 1,
          color,
          start,
          end,
          startStr: epochToTimeLabel(s),
          endStr: epochToTimeLabel(e),
          dur: end - start,
        });
      }
    }

    // The instance's wall-clock extent, folded from all its L2 descendants — not
    // just the steps surfaced as `events`. A run can have a duration but no
    // surfaced steps (e.g. its steps were dropped post-fold), and this still
    // recovers a time range to show for it.
    const extent = resolveCoverage(instance);

    return {
      trace: {
        name: processName,
        description: instance.goal ?? "",
        // Instances live in `nodes` and are always significant; the insignificant
        // `orphans` bucket is appended below as one synthetic `significance: false`
        // process.
        significance: true,
        events,
      },
      color,
      totalMin: (instance.duration_s ?? 0) / 60,
      eventCount: events.length,
      appSeconds: resolveAppSeconds(instance),
      extent:
        extent.length > 0
          ? { start: epochToIso(extent[0][0]), end: epochToIso(extent[extent.length - 1][1]) }
          : undefined,
    };
  });

  // Orphans are the L2/L3 nodes the pipeline never folded into a structured
  // process — "unstructured activity". They reuse the channel the old
  // insignificant processes used: a single synthetic process flagged
  // `significance: false`, with one timeline bar per coverage segment. This drives
  // the "Unstructured activity" timeline row and the donut's irrelevant slice,
  // while the significant process rows/stats skip it on the `significance` flag.
  const orphanNodes = data.orphans ?? [];
  if (orphanNodes.length > 0) {
    const orphanTraceIndex = processes.length;
    let orphanSeconds = 0;
    const orphanEvents: ReconstructedTrace["events"] = [];

    for (const orphan of orphanNodes) {
      orphanSeconds += orphan.duration_s ?? 0;
      const segments = resolveCoverage(orphan);
      if (segments.length === 0) continue;

      const name = orphan.name ?? "";
      orphanEvents.push({
        name,
        goal: orphan.goal ?? "",
        screenshots: orphan.depth === L3 ? stepScreenshots(orphan) : nodeScreenshots(orphan),
        start: epochToIso(segments[0][0]),
        end: epochToIso(segments[segments.length - 1][1]),
      });

      for (const [s, e] of segments) {
        const start = epochToWrappedMin(s);
        const end = epochToWrappedMin(e);
        allEvents.push({
          processName: name,
          traceIndex: orphanTraceIndex,
          eventName: name,
          eventIndex: orphanEvents.length - 1,
          color: INSIGNIFICANT_COLOR,
          start,
          end,
          startStr: epochToTimeLabel(s),
          endStr: epochToTimeLabel(e),
          dur: end - start,
        });
      }
    }

    if (orphanSeconds > 0 || orphanEvents.length > 0) {
      processes.push({
        trace: { name: "", description: "", significance: false, events: orphanEvents },
        color: INSIGNIFICANT_COLOR,
        totalMin: orphanSeconds / 60,
        eventCount: orphanEvents.length,
        appSeconds: new Map(),
      });
    }
  }

  allEvents.sort((a, b) => a.start - b.start);

  // Idle = the session span minus the union of every node's coverage. Both the
  // connected tree and the orphan bucket count toward "active": only L2 carries
  // coverage, so an orphan recovers it the same way a step does — directly if it
  // is L2, by folding its children if it is L3 (`resolveCoverage`).
  const coveredSegments: Interval[] = [];
  for (const node of data.nodes ?? []) coveredSegments.push(...nodeCoverage(node));
  for (const orphan of data.orphans ?? []) coveredSegments.push(...resolveCoverage(orphan));
  const active = mergeAdjacent(coveredSegments);

  const idleIntervals: Interval[] = [];
  let cursor = Number(data.startTimestamp);
  for (const [s, e] of active) {
    if (s > cursor) idleIntervals.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (cursor < Number(data.endTimestamp)) idleIntervals.push([cursor, Number(data.endTimestamp)]);

  // Only gaps of at least `IDLE_MIN_SECONDS` are real idle; shorter holes between
  // activity are dropped entirely — not counted, not drawn. If none survive, the
  // timeline's idle row drops out on its own (it renders only for non-empty input).
  const idleSegments: IdleSegment[] = idleIntervals
    .filter(([s, e]) => e - s >= IDLE_MIN_SECONDS)
    .map(([s, e]) => ({
      start: epochToWrappedMin(s),
      end: epochToWrappedMin(e),
      dur: (e - s) / 60,
      startLabel: epochToTimeLabel(s),
      endLabel: epochToTimeLabel(e),
    }));

  const totalActive = processes.reduce((acc, p) => acc + p.totalMin, 0);

  // App usage: sum app time across the frontier (L4 instances + orphans — the
  // nodes with no parent), the same shape as `duration_s` / `totalActive`, so app
  // slices and process slices share one counting basis. Instances reuse the
  // `appSeconds` already folded onto their ProcessView (the synthetic orphan
  // process carries an empty map, so it adds nothing here); the orphan nodes
  // themselves are resolved directly.
  const appSeconds = new Map<string, number>();
  const addApps = (apps: Map<string, number>) => {
    for (const [app, secs] of apps) {
      appSeconds.set(app, (appSeconds.get(app) ?? 0) + secs);
    }
  };
  for (const pv of processes) addApps(pv.appSeconds);
  for (const orphan of data.orphans ?? []) addApps(resolveAppSeconds(orphan));
  const appUsage: AppUsage[] = [...appSeconds.entries()]
    .map(([name, secs]) => ({ name, totalMin: secs / 60 }))
    .sort((a, b) => b.totalMin - a.totalMin);

  return {
    processes,
    idleSegments,
    allEvents,
    totalActive,
    appUsage,
    sessionSummary: data.sessionSummary ?? undefined,
  };
};
